import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { RiStopFill, RiMusic2Line, RiVolumeUpLine } from '@remixicon/react'
import { usePlayback } from '@/lib/playback-context'
import { stopMutationOptions } from '@/lib/queries/playback'

function formatTime(seconds: number): string {
	const m = Math.floor(seconds / 60)
	const s = seconds % 60
	return `${m}:${s.toString().padStart(2, '0')}`
}

export function NowPlayingBar() {
	const { state, elapsed, isPlaying } = usePlayback()
	const stopMutation = useMutation(stopMutationOptions())

	if (!isPlaying) return null

	return (
		<div className="now-playing-bar fixed inset-x-0 bottom-0 z-50 border-t border-border/50 bg-card/90 backdrop-blur-xl">
			{/* Subtle top glow line */}
			<div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

			<div className="mx-auto flex h-16 max-w-5xl items-center gap-4 px-4">
				{/* Song icon with equalizer animation */}
				<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
					<RiVolumeUpLine className="size-4 text-primary" />
				</div>

				{/* Song info */}
				<div className="min-w-0 flex-1">
					{state.playlistId ? (
						<Link
							to="/playlists/$id"
							params={{ id: state.playlistId }}
							className="group block min-w-0"
						>
							<p className="truncate font-serif text-sm font-semibold leading-tight text-foreground transition-colors group-hover:text-primary">
								{state.songName}
							</p>
							{state.songArtist && (
								<p className="mt-0.5 truncate text-[11px] text-muted-foreground/60">
									{state.songArtist}
								</p>
							)}
						</Link>
					) : (
						<div className="min-w-0">
							<p className="truncate font-serif text-sm font-semibold leading-tight text-foreground">
								{state.songName}
							</p>
							{state.songArtist && (
								<p className="mt-0.5 truncate text-[11px] text-muted-foreground/60">
									{state.songArtist}
								</p>
							)}
						</div>
					)}
				</div>

				{/* Elapsed time */}
				<div className="hidden items-center gap-3 sm:flex">
					<span className="font-mono text-xs tabular-nums text-muted-foreground/50">
						{formatTime(elapsed)}
					</span>
				</div>

				{/* Track position */}
				{state.totalSongs != null && state.songIndex != null && (
					<div className="hidden items-center gap-1.5 rounded-full bg-muted/50 px-2.5 py-1 md:flex">
						<RiMusic2Line className="size-3 text-muted-foreground/40" />
						<span className="text-[11px] tabular-nums text-muted-foreground/60">
							{state.songIndex + 1}
							<span className="mx-0.5 text-muted-foreground/30">/</span>
							{state.totalSongs}
						</span>
					</div>
				)}

				{/* Stop button */}
				<button
					type="button"
					onClick={() => stopMutation.mutate()}
					disabled={stopMutation.isPending}
					className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted/60 text-muted-foreground transition-all hover:bg-destructive/20 hover:text-destructive active:scale-95"
				>
					<RiStopFill className="size-3.5" />
				</button>
			</div>
		</div>
	)
}
